import { PAYMENT_METHODS_CODES } from "constants/paymentMethodsConst";
import {
  PaymentMethodType,
  PAYMENT_METHODS_ENUM,
  PAYMENT_SYSTEM_ENUM,
  PAYMENT_STEPS,
} from "types/PaymentMethodsTypes";

export type ServerPaymentMethod = {
  id: number;
  code: string;
  name: string;
  alias: string;
  logo: string;
  status: string;
  description: string;
  paymentSystemAlias: string;
  paymentMethodCurrencies: Array<{
    id: number;
    paymentMethodId: number;
    currencyId: number;
    status: string;
    apiSettings: any[];
    availableTransactionTypes: Array<{
      type: string;
      status: string;
      paymentMethodCurrencyId: number;
    }>;
    cashCommissions: Array<{
      id: number;
      userPercent: number;
      systemPercent: number;
      transactionType: string;
    }>;
    cashLimits: Array<{
      id: number;
      min: number;
      max: number;
      transactionType: string;
    }>;
    currency: {
      id: number;
      name: string;
      symbol: string;
      numericCode: number;
      isoCode: string;
    };
    depositInfo: {
      displayName: string;
      logoUrl: string;
      defaultSum: number;
      selectors: {
        min: number;
        med: number;
        max: number;
      };
      steps: string[] | [];
    };
    withdrawalInfo: {
      displayName: string;
      logoUrl: string;
      steps: string[] | [];
    };
    priorities: Array<{
      priority: number;
      type: string;
      paymentMethodCurrencyId: number;
      geolocationIsoCode: string;
    }>;
  }>;
  paymentSystem: {
    id: number;
    name: string;
    alias: string;
    description: string;
    status: string;
    paymentSystemAlias: string;
  };
};

const toPaymentMethodsCode = (code: string): PAYMENT_METHODS_CODES => {
  //   console.log(`Converting code to PAYMENT_METHODS_CODES: ${code}`);
  const foundCode = Object.values(PAYMENT_METHODS_CODES).find(
    (value) => value === code,
  );
  if (foundCode) {
    return foundCode as PAYMENT_METHODS_CODES;
  }
  //   console.error(`Invalid PAYMENT_METHODS_CODES: ${code}`);
  throw new Error(`Invalid PAYMENT_METHODS_CODES: ${code}`);
};

const toPaymentMethodsEnum = (alias: string): PAYMENT_METHODS_ENUM => {
  //   console.log(`Converting alias to PAYMENT_METHODS_ENUM: ${alias}`);
  const foundAlias = Object.values(PAYMENT_METHODS_ENUM).find(
    (value) => value === alias,
  );
  if (foundAlias) {
    return foundAlias as PAYMENT_METHODS_ENUM;
  }
  //   console.error(`Invalid PAYMENT_METHODS_ENUM: ${alias}`);
  throw new Error(`Invalid PAYMENT_METHODS_ENUM: ${alias}`);
};

const toPaymentSystemEnum = (alias: string): PAYMENT_SYSTEM_ENUM => {
  //   console.log(`Converting alias to PAYMENT_SYSTEM_ENUM: ${alias}`);
  const foundAlias = Object.values(PAYMENT_SYSTEM_ENUM).find(
    (value) => value === alias,
  );
  if (foundAlias) {
    return foundAlias as PAYMENT_SYSTEM_ENUM;
  }
  //   console.error(`Invalid PAYMENT_SYSTEM_ENUM: ${alias}`);
  throw new Error(`Invalid PAYMENT_SYSTEM_ENUM: ${alias}`);
};

const toPaymentStep = (step: string): PAYMENT_STEPS => {
  //   console.log(`Converting step to PAYMENT_STEPS: ${step}`);
  const foundStep = Object.values(PAYMENT_STEPS).find(
    (value) => value === step,
  );
  if (foundStep) {
    return foundStep as PAYMENT_STEPS;
  }
  //   console.warn(`Invalid PAYMENT_STEP: ${step}, returning as "UNKNOWN"`);
  return "UNKNOWN" as PAYMENT_STEPS;
};

export const transformPaymentMethods = (
  serverMethods: ServerPaymentMethod[],
  type: "deposit" | "withdraw",
): PaymentMethodType[] => {
  //   console.log(`Transforming payment methods for type: ${type}`);
  //   console.log("Received serverMethods:", serverMethods);

  return serverMethods
    .map((method) => {
      try {
        // console.log(`Processing method: ${method.name} (${method.code})`);

        if (
          !method.paymentMethodCurrencies ||
          method.paymentMethodCurrencies.length === 0
        ) {
          //   console.warn(
          //     `Skipping method: ${method.name} due to missing currencies`,
          //   );
          return null;
        }

        const paymentMethodCurrency = method.paymentMethodCurrencies[0];

        // console.log("Selected paymentMethodCurrency:", paymentMethodCurrency);

        const depositLimits = paymentMethodCurrency.cashLimits.find(
          (limit) => limit.transactionType === "DEPOSIT",
        );

        const withdrawLimits = paymentMethodCurrency.cashLimits.find(
          (limit) => limit.transactionType === "WITHDRAW",
        );

        // console.log("Deposit limits:", depositLimits);
        // console.log("Withdraw limits:", withdrawLimits);

        const isDeposit = type === "deposit";
        // console.log(`Is deposit type: ${isDeposit}`);

        if (isDeposit && !paymentMethodCurrency.depositInfo) {
          //   console.warn(
          //     `Skipping deposit method: ${method.name} due to missing depositInfo`,
          //   );
          return null;
        }

        if (!isDeposit && !paymentMethodCurrency.withdrawalInfo) {
          //   console.warn(
          //     `Skipping withdrawal method: ${method.name} due to missing withdrawalInfo`,
          //   );
          return null;
        }

        if (!method.paymentSystem || !method.paymentSystem.alias) {
          //   console.warn(
          //     `Skipping method: ${method.name} due to missing paymentSystem`,
          //   );
          return null;
        }

        const transformedMethod: PaymentMethodType = {
          code: toPaymentMethodsCode(method.code),
          slug: toPaymentMethodsEnum(method.alias),
          icon: isDeposit
            ? paymentMethodCurrency.depositInfo.logoUrl
            : paymentMethodCurrency.withdrawalInfo.logoUrl,
          name: isDeposit
            ? paymentMethodCurrency.depositInfo.displayName
            : paymentMethodCurrency.withdrawalInfo.displayName,
          system: toPaymentSystemEnum(method.paymentSystem.paymentSystemAlias),
          limit: {
            deposit: {
              min: depositLimits?.min || 0,
              max: depositLimits?.max || 0,
            },
            withdraw: {
              min: withdrawLimits?.min || 0,
              max: withdrawLimits?.max || 0,
            },
          },
          amountPreset: isDeposit
            ? [
                paymentMethodCurrency.depositInfo.selectors.min.toString(),
                paymentMethodCurrency.depositInfo.selectors.med.toString(),
                paymentMethodCurrency.depositInfo.selectors.max.toString(),
              ]
            : [],
          defaultValue: isDeposit
            ? paymentMethodCurrency.depositInfo.defaultSum.toString()
            : "",
          status: method.status,
          steps: {
            deposit: isDeposit
              ? paymentMethodCurrency.depositInfo.steps.map(toPaymentStep)
              : [],
            withdraw: !isDeposit
              ? paymentMethodCurrency.withdrawalInfo.steps.map(toPaymentStep)
              : [],
          },
        };

        // console.log("Transformed method:", transformedMethod);
        return transformedMethod;
      } catch (error) {
        // console.warn(
        //   `Skipping method: ${method.name} due to processing error: ${error.message}`,
        // );
        return null;
      }
    })
    .filter((method): method is PaymentMethodType => method !== null);
};
